// Modules
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Checkbox, FormGroup, InputGroup, Intent } from '@blueprintjs/core'
import axios from 'axios'

// Components
import InputNotice from 'components/interface/input-notice/InputNotice'
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'
import ProviderButton from 'components/external/provider-button/ProviderButton'
import TOSDialog from './components/tos-dialog/TOSDialog'

// Hooks
import useFormControls from 'hooks/validation/useFormControls'
import useLanguage from 'hooks/system/useLanguage'
import useNotices from 'hooks/system/useNotices'
import useRegister from 'hooks/authentication/useRegister'

// Helpers
import { resetURLSearchParameters } from 'helpers/route'

// Utilities
import { uppercaseFirst } from 'utilities/text'

// Constants
import { CAPTCHA_V2_SITE_KEY, CAPTCHA_V3_SITE_KEY, EMAIL_VALIDATION_CHECK_URL, IS_ON_PREMISE, SSO_PROVIDERS } from 'constants/environment'

// Styles
import './Form.scss'

// Data (Local)
const willUseCaptcha = !IS_ON_PREMISE

// Section: Register > Landing > Form
function Form () {
	// References
	const captcha = useRef()

	// State
	const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', password: '', passwordConfirmation: '' })
	const [isInitialized, setIsInitialized] = useState(false)
	const [isPasswordVisible, setIsPasswordVisible] = useState(false)
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [stage, setStage] = useState('account-information') // STAGES: 1. account-information, 2. profile-details
	const [captchaToken, setCaptchaToken] = useState(null)
	const [emailErrorCode, setEmailErrorCode] = useState(null)
	const [isValidating, setIsValidating] = useState(false)
	const [isCaptchaExpired, setIsCaptchaExpired] = useState(false)
	const [isCaptchaReady, setIsCaptchaReady] = useState(false)
	const [isTOSAccepted, setIsTOSAccepted] = useState(false)
	const [isTOSDialogOpen, setIsTOSDialogOpen] = useState(false)

	// Data (Memoized)
	const willShowValidationIssues = useMemo(() => !emailErrorCode && isValidating, [emailErrorCode, isValidating])
	const parameters = useMemo(() => willUseCaptcha && !!captchaToken ? { headers: { recaptcha: captchaToken } } : null, [captchaToken])

	// Data
	const { language, translate } = useLanguage('pages.register.views.landing.sections.form')
	const { addNotice } = useNotices()
	const {
		isValid: isEmailValid,
		issues: emailIssues
	} = useFormControls(['isEmail'], formData.email)
	const {
		isValid: isPasswordValid,
		issues: passwordIssues
	} = useFormControls(['hasAtLeast8Characters', 'hasLowerCaseLetter', 'hasNumber', 'hasSpecialCharacter', 'hasUpperCaseLetter'], formData.password)
	const {
		isValid: isPasswordConfirmationValid,
		issues: passwordConfirmationIssues
	} = useFormControls(['hasMatchingValue'], formData.passwordConfirmation, formData.password)
	const {
		isValid: isFirstNameValid,
		issues: firstNameIssues
	} = useFormControls(['isPersonName'], formData.firstName)
	const {
		isValid: isLastNameValid,
		issues: lastNameIssues
	} = useFormControls(['isPersonName'], formData.lastName)
	const { error, isProcessing, register } = useRegister()
	const isAccountInformationFormReady = useMemo(() => (
		stage === 'account-information' &&
		(isEmailValid && isPasswordValid && isPasswordConfirmationValid)
	), [isEmailValid, isPasswordConfirmationValid, isPasswordValid, stage])
	const isProfileDetailsFormReady = (
		stage === 'profile-details' &&
		(isFirstNameValid && isLastNameValid)
	)

	// Functions
	function handleInput (type, value) {
		if (type === 'firstName' || type === 'lastName') {
			value = value
				.replace(/\s+/g, ' ')
				.split(' ')
				.map(name => uppercaseFirst(name, { locale: language }))
				.join(' ')
		}
		setFormData(formData => ({ ...formData, [type]: value }))
	}
	function handleSubmit (event) {
		event.preventDefault()
		if (isProfileDetailsFormReady) {
			setFormData(formData => ({ ...formData, firstName: formData.firstName.trim(), lastName: formData.lastName.trim() }))
			if (willUseCaptcha) {
				window.grecaptcha.ready(() => {
					window.grecaptcha.execute(CAPTCHA_V3_SITE_KEY, { action: 'registration' }) // Recaptcha v3 is being executed in order to train it
					window.grecaptcha.execute()
				})
			}
		} else setIsValidating(true)
	}
	function togglePasswordVisibility () {
		setIsPasswordVisible(isPasswordVisible => !isPasswordVisible)
	}

	// Functions (Memoized)
	const onResponse = useCallback((response, provider) => register(response, { service: provider }), [register])

	// Effects
	useEffect(() => {
		resetURLSearchParameters()
		setTimeout(() => setIsInitialized(true), 1000)
	}, [])
	useEffect(() => {
		if (willUseCaptcha) {
			let script
			if (!document.getElementById('google-recaptcha-script')) {
				script = document.createElement('script')
				script.src = `https://www.google.com/recaptcha/api.js?render=${CAPTCHA_V3_SITE_KEY}&badge=bottomleft`
				script.id = 'google-recaptcha-script'
				script.onload = () => setIsCaptchaReady(true)
				document.head.appendChild(script)
			}
			return () => {
				if (!!script) document.head.removeChild(script)
				const badge = document.getElementsByClassName('grecaptcha-badge')?.[0]?.parentElement
				if (!!badge) badge.parentElement.removeChild(badge)
				const prompt = document.querySelector('iframe[src*="recaptcha"]')?.parentElement?.parentElement
				if (!!prompt) prompt.parentElement.removeChild(prompt)
			}
		}
	}, [])
	useEffect(() => {
		if (willUseCaptcha) {
			window.onRecaptchaResponse = token => {
				setCaptchaToken(token)
				if (isProfileDetailsFormReady) setIsSubmitted(true)
			}
			window.onRecaptchaExpire = () => {
				addNotice({
					action: { onClick: () => window.location.reload(), text: translate.global('actions.refreshPage') }, // WARNING: "onClick: window.location.reload" causes "Illegal invocation" error
					code: 'client.capthchaExpiredError',
					type: 'error',
					willPersist: true
				})
				setIsCaptchaExpired(true)
			}
		} else if (isProfileDetailsFormReady) setIsSubmitted(true)
	}, [addNotice, isProfileDetailsFormReady, translate])
	useEffect(() => {
		if (willUseCaptcha && isCaptchaReady) window.grecaptcha.ready(() => window.grecaptcha.render(captcha.current, { sitekey: CAPTCHA_V2_SITE_KEY, size: 'invisible' }))
	}, [isCaptchaReady]) // WARNING: Do not add more dependency
	useEffect(() => {
		if (!!error) setIsSubmitted(false)
	}, [error])
	useEffect(() => {
		let timer
		async function checkEmailValidation () {
			try {
				await axios({ data: { email: formData?.email }, method: 'POST', url: EMAIL_VALIDATION_CHECK_URL })
			} catch (error) {
				const errorCode = Array.isArray(error.response?.data?.message) ? error.response.data.message[0] : error.response?.data?.message
				setEmailErrorCode(errorCode)
			}
		}
		setEmailErrorCode(null)
		if (isEmailValid) timer = setTimeout(checkEmailValidation, 500)
		return () => clearTimeout(timer)
	}, [formData?.email, isEmailValid])
	useEffect(() => {
		// NOTE: Using an effect instead of event based flow because of the recaptcha system
		if (isSubmitted) register(formData, { parameters })
	}, [formData, isSubmitted, parameters, register])

	// Render
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo
					className="aix-logo"
					isCommonColor={!isInitialized} />
				<div className="aix-content">
					<form className="aix-form aix-paper-thick">
						<h2 className="aix-title">
							{stage === 'account-information' &&
								<span data-label={translate.global('generic.step', { number: 1 })}>
									{translate.local('form.title.accountInformation')}
								</span>
							}
							{stage === 'profile-details' &&
								<span data-label={translate.global('generic.step', { number: 2 })}>
									{translate.local('form.title.profileDetails')}
								</span>
							}
						</h2>
						{stage === 'account-information' &&
							<>
								<FormGroup
									intent={Intent[!!emailErrorCode ? 'DANGER' : 'NONE']}
									helperText={!!emailErrorCode ? translate.global(`notices.error.server.${emailErrorCode}`) : null}>
									<InputNotice
										intent={Intent.WARNING}
										list={
											willShowValidationIssues && emailIssues.length > 0
												? translate.global(`notices.warning.form.${emailIssues[0]}Warning`)
												: []
										}>
										<InputGroup
											type="email"
											name="email"
											autoComplete="off"
											disabled={isProcessing}
											value={formData.email}
											className="aix-input aix-email"
											intent={Intent[!!emailErrorCode ? 'DANGER' : ((!willShowValidationIssues || isEmailValid) ? 'NONE' : 'WARNING')]}
											placeholder={translate.local('form.fields.email.placeholder')}
											onChange={({ target }) => handleInput('email', target.value)} />
									</InputNotice>
								</FormGroup>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={
											willShowValidationIssues && passwordIssues.length > 0
												? translate.global(`notices.warning.form.${passwordIssues[0]}Warning`)
												: []
										}>
										<InputGroup
											name="password"
											autoComplete="off"
											disabled={isProcessing}
											value={formData.password}
											type={isPasswordVisible ? 'text' : 'password'}
											rightElement={
												<Button
													minimal={true}
													disabled={isProcessing}
													className="aix-password-visibility"
													icon={isPasswordVisible ? 'eye-off' : 'eye-open'}
													intent={isPasswordVisible ? Intent.WARNING : Intent.DANGER}
													onClick={togglePasswordVisibility} />
											}
											className="aix-input aix-password"
											intent={Intent[(!willShowValidationIssues || isPasswordValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.password.placeholder')}
											onChange={({ target }) => handleInput('password', target.value)} />
											{/* TODO: Add password strength here */}
									</InputNotice>
								</FormGroup>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={
											willShowValidationIssues && passwordIssues.length === 0 && passwordConfirmationIssues.length > 0
												? translate.global(`notices.warning.form.${passwordConfirmationIssues[0]}Warning`)
												: []
										}>
										<InputGroup
											name="password-confirmation"
											autoComplete="off"
											disabled={isProcessing}
											value={formData.passwordConfirmation}
											type={isPasswordVisible ? 'text' : 'password'}
											className="aix-input aix-password"
											intent={Intent[(!willShowValidationIssues || passwordIssues.length > 0 || isPasswordConfirmationValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.passwordConfirmation.placeholder')}
											onChange={({ target }) => handleInput('passwordConfirmation', target.value)} />
									</InputNotice>
								</FormGroup>
								<Button
									fill
									type="submit"
									loading={isProcessing}
									className="aix-submit bp4-large"
									disabled={(willUseCaptcha && isCaptchaExpired) || isProcessing || !!emailErrorCode || !isTOSAccepted}
									onClick={event => {
										event.preventDefault()
										setIsValidating(!isAccountInformationFormReady)
										if (isAccountInformationFormReady) setStage('profile-details')
									}}>
									{translate.local('form.actions.continue')}
								</Button>
								<Checkbox
									className="aix-tos-confirmation"
									checked={isTOSAccepted}
									label={<span>I accept the <a className="aix-tos-link">Terms of Service</a>.</span>}
									onChange={() => {
										if (isTOSAccepted) setIsTOSAccepted(false)
										else setIsTOSDialogOpen(true)
									}} />
								{isTOSDialogOpen &&
									<TOSDialog
										isTOSAccepted={isTOSAccepted}
										confirm={() => {
											setIsTOSAccepted(true)
											setIsTOSDialogOpen(false)
										}}
										cancel={() => {
											setIsTOSAccepted(false)
											setIsTOSDialogOpen(false)
										}} />
								}
							</>
						}
						{stage === 'profile-details' &&
							<>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={
											willShowValidationIssues && firstNameIssues.length > 0
												? translate.global(`notices.warning.form.${firstNameIssues[0]}Warning`)
												: []
										}>
										<InputGroup
											type="text"
											name="firstName"
											disabled={isProcessing}
											value={formData.firstName}
											className="aix-input aix-first-name"
											intent={Intent[(!willShowValidationIssues || isFirstNameValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.firstName.placeholder')}
											onChange={({ target }) => handleInput('firstName', target.value)} />
									</InputNotice>
								</FormGroup>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={
											willShowValidationIssues && lastNameIssues.length > 0
												? translate.global(`notices.warning.form.${lastNameIssues[0]}Warning`)
												: []
										}>
										<InputGroup
											type="text"
											name="lastName"
											disabled={isProcessing}
											value={formData.lastName}
											className="aix-input aix-last-name"
											intent={Intent[(!willShowValidationIssues || isLastNameValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.lastName.placeholder')}
											onChange={({ target }) => handleInput('lastName', target.value)} />
									</InputNotice>
								</FormGroup>
								<Button
									fill
									type="submit"
									loading={isProcessing}
									className="aix-submit bp4-large"
									disabled={isProcessing}
									onClick={handleSubmit}>
									{translate.local('form.actions.register')}
								</Button>
							</>
						}
					</form>
					{stage === 'account-information' &&
						<>
							{SSO_PROVIDERS?.length > 0 &&
								<ul className="aix-actions">
									{SSO_PROVIDERS.map(provider => (
										<li
											key={provider}
											className="aix-action">
											<ProviderButton
												type="continue"
												provider={provider}
												isDisabled={isProcessing}
												isInitialized={isInitialized}
												onResponse={onResponse} />
										</li>
									))}
								</ul>
							}
							<small className="aix-options">{translate.local('form.options.login.label')} <Link to="/login">{translate.local('form.options.login.action')}</Link></small>
						</>
					}
				</div>
				{willUseCaptcha &&
					<div
						ref={captcha}
						data-expired-callback="onRecaptchaExpire"
						data-callback="onRecaptchaResponse">
					</div>
				}
			</div>
		</Page.Section>
	)
}

// Export
export default Form
